import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './MyEmployeesSection.css';
import {
  deleteLocalEmployee,
  listLocalEmployees,
  LocalEmployee,
  upsertLocalEmployee,
} from '../../services/localEmployeesStore';
import DataPackagesModal from './DataPackagesModal';
import ReliabilityModal from './ReliabilityModal';
import DualRangeSlider from './DualRangeSlider';
import AumBucketFilter from './AumBucketFilter';
import { ALL_AUM_BUCKET_IDS, aumMatchesSelectedBuckets, type AumBucketId } from './aumFilterBuckets';
import { getMarketDataByPackageName, MarketDataRecord } from '../../services/marketDataService';
import { parseOptionalFiniteNumber } from '../../utils/optionalNumber';
import { EmployeeMarketComparisonList } from './EmployeeMarketComparisonList';
import { fetchCommunityGradingTreeCached } from '../../services/gradingService';
import {
  listEmployeeGradingOverrides,
  listPositionGradings,
  type EmployeeGradingOverride,
  type PositionGrading,
} from '../../services/localEmployeeGradingStore';
import { buildRoleClusterMetaList } from '../../utils/marketClusterMeta';
import { seedMyEmployeesTestData } from '../../utils/seedMyEmployeesTestData';
import type { GradingSavedHighlight } from './employeeGrading/EmployeeGradingSection';
import type { MarketGroupingMode } from '../../utils/marketGroupUtils';
import type { GradingTreePayload } from './admin/grading/types';

function newId(): string {
  // Prefer crypto.randomUUID where available (modern browsers).
  // Fallback keeps IDs stable enough for local usage.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `emp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function clamp(num: number, min: number, max: number) {
  return Math.min(max, Math.max(min, num));
}

const MIN_SELECTED_COMPANIES = 10;

const RELIABILITY_LEVELS = [
  { id: 'Excellent' as const, label: 'Очень высокая (Excellent)', min: 0.8, max: 1, color: '#073763' },
  { id: 'High' as const, label: 'Высокая (High)', min: 0.5, max: 0.8, color: '#1155cc' },
  { id: 'Moderate' as const, label: 'Умеренная (Moderate)', min: 0.3, max: 0.5, color: '#6d9eeb' },
  { id: 'Low' as const, label: 'Низкая (Low)', min: 0, max: 0.3, color: '#999999' },
] as const;

type ReliabilityLevelId = typeof RELIABILITY_LEVELS[number]['id'];

function getReliabilityLevelForScore(score: number): ReliabilityLevelId {
  if (score > 0.8) return 'Excellent';
  if (score > 0.5) return 'High';
  if (score > 0.3) return 'Moderate';
  return 'Low';
}

type CompanyItem = { id: string; name: string };

type PositionRoleRow = {
  key: string;
  position: string;
  roleName: string | null;
  familyName: string | null;
  employeeCount: number;
};

function buildPositionRoleRows(
  employees: LocalEmployee[],
  positionGradings: PositionGrading[],
): PositionRoleRow[] {
  const gradingByKey = new Map<string, PositionGrading>();
  positionGradings.forEach((grading) => {
    gradingByKey.set(grading.position.trim().toLowerCase(), grading);
  });

  const groups = new Map<string, { position: string; employeeCount: number }>();
  employees.forEach((employee) => {
    const trimmed = employee.position.trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    const existing = groups.get(key);
    if (existing) {
      existing.employeeCount += 1;
    } else {
      groups.set(key, { position: trimmed, employeeCount: 1 });
    }
  });

  return Array.from(groups.entries())
    .map(([key, { position, employeeCount }]) => {
      const grading = gradingByKey.get(key);
      return {
        key,
        position,
        roleName: grading?.roleName ?? null,
        familyName: grading?.familyName ?? null,
        employeeCount,
      };
    })
    .sort((a, b) => a.position.localeCompare(b.position, 'ru'));
}

type RowHighlight = {
  positionKey: string | null;
  employeeId: string | null;
};

const HIGHLIGHT_CELL_STYLE: React.CSSProperties = {
  backgroundColor: '#e8f5e9',
  color: '#2e7d32',
  fontWeight: 600,
};

type MyEmployeesSectionProps = {
  embedded?: boolean;
  onGradeEmployee?: (employee: LocalEmployee) => void;
  onGradePosition?: (position: string) => void;
  gradingSavedToken?: number;
  gradingSavedHighlight?: GradingSavedHighlight | null;
  onClearHighlight?: () => void;
};

const MyEmployeesSection: React.FC<MyEmployeesSectionProps> = ({
  embedded = false,
  onGradeEmployee,
  onGradePosition,
  gradingSavedToken = 0,
  gradingSavedHighlight = null,
  onClearHighlight,
}) => {
  const [employees, setEmployees] = useState<LocalEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [position, setPosition] = useState('');
  const [fixMonthlyGross, setFixMonthlyGross] = useState<string>('');
  const [stiPercent, setStiPercent] = useState<string>(''); // UI input, stored as fraction
  const [ltip, setLtip] = useState<'yes' | 'no'>('no');

  const [isEmployeesListExpanded, setIsEmployeesListExpanded] = useState(false);

  // Market comparison state (filters live here so comparison matches them)
  const [isMarketModalOpen, setIsMarketModalOpen] = useState(false);
  const [selectedPackageDisplayName, setSelectedPackageDisplayName] = useState<string>('');
  const [marketData, setMarketData] = useState<MarketDataRecord[]>([]);
  const [filteredData, setFilteredData] = useState<MarketDataRecord[]>([]);
  const [marketLoading, setMarketLoading] = useState(false);
  const [filterMode, setFilterMode] = useState<'sector' | 'company'>('sector');
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedReliabilityLevels, setSelectedReliabilityLevels] = useState<ReliabilityLevelId[]>(['Excellent', 'High', 'Moderate', 'Low']);
  const [selectedAumBuckets, setSelectedAumBuckets] = useState<AumBucketId[]>([...ALL_AUM_BUCKET_IDS]);
  const [employeeBounds, setEmployeeBounds] = useState<[number, number]>([0, 0]);
  const [employeeRange, setEmployeeRange] = useState<[number, number]>([0, 0]);
  const [isReliabilityModalOpen, setIsReliabilityModalOpen] = useState(false);
  const [positionGradings, setPositionGradings] = useState<PositionGrading[]>([]);
  const [employeeGradingOverrides, setEmployeeGradingOverrides] = useState<EmployeeGradingOverride[]>([]);
  const [gradingTree, setGradingTree] = useState<GradingTreePayload | null>(null);
  const [comparisonGroupingMode, setComparisonGroupingMode] = useState<MarketGroupingMode>('grade');
  const [seedingTestData, setSeedingTestData] = useState(false);
  const [seedMessage, setSeedMessage] = useState('');
  const [rowHighlight, setRowHighlight] = useState<RowHighlight | null>(null);
  const highlightedRoleRowRef = useRef<HTMLTableRowElement>(null);
  const highlightedEmployeeRowRef = useRef<HTMLTableRowElement>(null);
  const employeeFormCardRef = useRef<HTMLDivElement>(null);
  const fullNameInputRef = useRef<HTMLInputElement>(null);
  const editingEmployeeRowRef = useRef<HTMLTableRowElement>(null);

  const isEditing = editingId !== null;

  const editingEmployee = useMemo(
    () => (editingId ? employees.find((e) => e.id === editingId) ?? null : null),
    [editingId, employees],
  );

  const resetForm = () => {
    setEditingId(null);
    setFullName('');
    setPosition('');
    setFixMonthlyGross('');
    setStiPercent('');
    setLtip('no');
  };

  const loadGradings = async () => {
    try {
      const [positions, overrides] = await Promise.all([
        listPositionGradings(),
        listEmployeeGradingOverrides(),
      ]);
      setPositionGradings(positions);
      setEmployeeGradingOverrides(overrides);
    } catch (e) {
      console.error(e);
    }
  };

  const loadEmployees = async () => {
    try {
      setError('');
      setLoading(true);
      const list = await listLocalEmployees();
      setEmployees(list);
      await loadGradings();
    } catch (e) {
      console.error(e);
      setError('Не удалось загрузить сотрудников из локального хранилища.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (!gradingSavedToken || !gradingSavedHighlight) return;

    setRowHighlight({
      positionKey: gradingSavedHighlight.kind === 'position' ? gradingSavedHighlight.positionKey : null,
      employeeId: gradingSavedHighlight.kind === 'employee' ? gradingSavedHighlight.employeeId : null,
    });
    if (gradingSavedHighlight.kind === 'employee') {
      setIsEmployeesListExpanded(true);
    }
    void loadGradings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradingSavedToken, gradingSavedHighlight]);

  useEffect(() => {
    if (!rowHighlight) return;

    const frame = window.requestAnimationFrame(() => {
      if (rowHighlight.positionKey) {
        highlightedRoleRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      if (rowHighlight.employeeId) {
        highlightedEmployeeRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });

    let clearTimer: number | undefined;
    const attachClearListenerTimer = window.setTimeout(() => {
      const clearHighlight = () => {
        setRowHighlight(null);
        onClearHighlight?.();
      };

      const onClick = () => {
        clearHighlight();
      };

      document.addEventListener('click', onClick, { once: true, capture: true });
      clearTimer = window.setTimeout(() => {
        document.removeEventListener('click', onClick, { capture: true });
      }, 60000);
    }, 500);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(attachClearListenerTimer);
      if (clearTimer) window.clearTimeout(clearTimer);
    };
  }, [rowHighlight, onClearHighlight]);

  useEffect(() => {
    if (!selectedPackageDisplayName) {
      setGradingTree(null);
      return;
    }

    let cancelled = false;
    fetchCommunityGradingTreeCached()
      .then((data) => {
        if (!cancelled) setGradingTree(data);
      })
      .catch(() => {
        if (!cancelled) setGradingTree(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPackageDisplayName]);

  const clusterMetas = useMemo(
    () => (gradingTree ? buildRoleClusterMetaList(gradingTree) : []),
    [gradingTree],
  );
  const clusterGroupingReady = clusterMetas.length > 0;

  const onEdit = (emp: LocalEmployee) => {
    setRowHighlight(null);
    onClearHighlight?.();
    setEditingId(emp.id);
    setFullName(emp.fullName);
    setPosition(emp.position);
    setFixMonthlyGross(String(emp.fixMonthlyGross));
    setStiPercent(String(Math.round(emp.stiFraction * 1000) / 10)); // e.g. 0.253 -> 25.3
    setLtip(emp.ltip ? 'yes' : 'no');
  };

  const onDelete = async (id: string) => {
    if (!window.confirm('Удалить сотрудника?')) return;
    try {
      await deleteLocalEmployee(id);
      await loadEmployees();
      if (editingId === id) resetForm();
    } catch (e) {
      console.error(e);
      setError('Не удалось удалить сотрудника.');
    }
  };

  const onSeedTestData = async () => {
    try {
      setSeedingTestData(true);
      setError('');
      setSeedMessage('');
      const result = await seedMyEmployeesTestData();
      await loadEmployees();
      if (result.gradingSkipped) {
        setSeedMessage(
          `Добавлено ${result.employeesUpserted} тестовых сотрудников. Грейды не созданы — не удалось загрузить дерево грейдинга.`,
        );
      } else {
        setSeedMessage(
          `Добавлено ${result.employeesUpserted} сотрудников (${result.gradingsUpserted} должностей с грейдом).`,
        );
      }
    } catch (e) {
      console.error(e);
      setError('Не удалось заполнить тестовыми данными.');
    } finally {
      setSeedingTestData(false);
    }
  };

  const submitLabel = isEditing ? 'Сохранить изменения' : 'Добавить';

  const canSubmit = useMemo(() => {
    if (!fullName.trim()) return false;
    if (!position.trim()) return false;
    const fix = Number(fixMonthlyGross);
    const sti = Number(stiPercent);
    if (!Number.isFinite(fix) || fix < 0) return false;
    if (!Number.isFinite(sti) || sti < 0) return false;
    return true;
  }, [fullName, position, fixMonthlyGross, stiPercent]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setError('');
      const now = new Date().toISOString();

      const fix = Number(fixMonthlyGross);
      const stiPct = Number(stiPercent);
      const stiFraction = clamp(stiPct / 100, 0, 10); // allow up to 1000% if someone insists

      const normalizedPosition = position.trim();

      const emp: LocalEmployee = {
        id: editingId ?? newId(),
        fullName: fullName.trim(),
        position: normalizedPosition,
        fixMonthlyGross: fix,
        stiFraction,
        ltip: ltip === 'yes',
        createdAt: isEditing
          ? (employees.find((x) => x.id === editingId)?.createdAt ?? now)
          : now,
        updatedAt: now,
      };

      const savedEmployeeId = isEditing ? editingId : null;

      await upsertLocalEmployee(emp);
      await loadEmployees();
      resetForm();

      if (savedEmployeeId) {
        setIsEmployeesListExpanded(true);
        setRowHighlight({ positionKey: null, employeeId: savedEmployeeId });
      }
    } catch (err) {
      console.error(err);
      setError('Не удалось сохранить сотрудника.');
    }
  };

  const countText = useMemo(() => {
    const n = employees.length;
    const word =
      n % 10 === 1 && n % 100 !== 11
        ? 'сотрудник'
        : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)
          ? 'сотрудника'
          : 'сотрудников';
    return `${n} ${word}`;
  }, [employees.length]);

  const employeesVisibleLimit = 5;
  const visibleEmployees = useMemo(() => {
    if (isEmployeesListExpanded) return employees;
    return employees.slice(0, employeesVisibleLimit);
  }, [employees, isEmployeesListExpanded]);

  useEffect(() => {
    if (!editingId) return;
    const idx = employees.findIndex((e) => e.id === editingId);
    if (idx >= employeesVisibleLimit && !isEmployeesListExpanded) {
      setIsEmployeesListExpanded(true);
    }
  }, [editingId, employees, isEmployeesListExpanded, employeesVisibleLimit]);

  useEffect(() => {
    if (!editingId) return;

    let focusTimer: number | undefined;
    const frame = window.requestAnimationFrame(() => {
      employeeFormCardRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      editingEmployeeRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      focusTimer = window.setTimeout(() => {
        fullNameInputRef.current?.focus({ preventScroll: true });
      }, 350);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (focusTimer !== undefined) window.clearTimeout(focusTimer);
    };
  }, [editingId, isEmployeesListExpanded]);

  const positionRoleRows = useMemo(
    () => buildPositionRoleRows(employees, positionGradings),
    [employees, positionGradings],
  );

  useEffect(() => {
    if (!rowHighlight?.positionKey) return;
    const frame = window.requestAnimationFrame(() => {
      highlightedRoleRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [rowHighlight?.positionKey, positionRoleRows]);

  useEffect(() => {
    if (!rowHighlight?.employeeId || loading) return;
    const frame = window.requestAnimationFrame(() => {
      highlightedEmployeeRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [rowHighlight?.employeeId, loading, visibleEmployees]);

  const handleSelectMarketPackage = async (packageName: string, packageDisplayName: string) => {
    setMarketLoading(true);
    setSelectedPackageDisplayName(packageDisplayName);
    try {
      const data = await getMarketDataByPackageName(packageName);
      setMarketData(data);
      setFilteredData(data);
      const uniqSectors = Array.from(new Set(data.map((r) => r.sector))).filter(Boolean);
      setSelectedSectors(uniqSectors);
      setSelectedCompanies([]);
      setSelectedReliabilityLevels(['Excellent', 'High', 'Moderate', 'Low']);
      setSelectedAumBuckets([...ALL_AUM_BUCKET_IDS]);
      const empValues = data
        .map((r) => parseOptionalFiniteNumber(r.employee_amount))
        .filter((v): v is number => v !== null);
      const nextEmployeeBounds: [number, number] = empValues.length
        ? [Math.floor(Math.min(...empValues)), Math.ceil(Math.max(...empValues))]
        : [0, 0];
      setEmployeeBounds(nextEmployeeBounds);
      setEmployeeRange(nextEmployeeBounds);
    } catch (e) {
      console.error(e);
      setMarketData([]);
      setFilteredData([]);
      setSelectedSectors([]);
      setSelectedCompanies([]);
      setSelectedAumBuckets([...ALL_AUM_BUCKET_IDS]);
      setEmployeeBounds([0, 0]);
      setEmployeeRange([0, 0]);
    } finally {
      setMarketLoading(false);
    }
  };

  const companiesBySector = useMemo((): Map<string, CompanyItem[]> => {
    const map = new Map<string, CompanyItem[]>();
    const seen = new Set<string>();
    marketData.forEach((record) => {
      if (!record.sector) return;
      const id = record.company_id;
      if (seen.has(id)) return;
      seen.add(id);
      const name = record.company_name || record.company_id;
      const list = map.get(record.sector) || [];
      list.push({ id, name });
      map.set(record.sector, list);
    });
    const entries = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    const sorted = new Map<string, CompanyItem[]>(entries);
    sorted.forEach((list: CompanyItem[]) => list.sort((a: CompanyItem, b: CompanyItem) => a.name.localeCompare(b.name)));
    return sorted;
  }, [marketData]);

  const datasetMissingEmployeeScale = useMemo(() => {
    if (marketData.length === 0) return false;
    return !marketData.some((r) => parseOptionalFiniteNumber(r.employee_amount) !== null);
  }, [marketData]);

  const applyMarketFilters = () => {
    let data: MarketDataRecord[];
    if (filterMode === 'sector') {
      if (selectedSectors.length === 0) {
        setFilteredData([]);
        return;
      }
      data = marketData.filter((r) => selectedSectors.includes(r.sector));
    } else {
      if (selectedCompanies.length < MIN_SELECTED_COMPANIES) {
        setFilteredData([]);
        return;
      }
      data = marketData.filter((r) => selectedCompanies.includes(r.company_id));
    }

    if (selectedReliabilityLevels.length === 0) {
      setFilteredData([]);
      return;
    }
    data = data.filter((r) => selectedReliabilityLevels.includes(getReliabilityLevelForScore(r.reliability_score)));
    if (filterMode === 'sector') {
      data = data.filter((record) => {
        const aum = parseOptionalFiniteNumber(record.aum);
        const employeeAmount = parseOptionalFiniteNumber(record.employee_amount);
        const aumOk = aumMatchesSelectedBuckets(aum, selectedAumBuckets);
        const employeeOk =
          employeeAmount === null ||
          (employeeAmount >= employeeRange[0] && employeeAmount <= employeeRange[1]);
        return aumOk && employeeOk;
      });
    }
    setFilteredData(data);
  };

  useEffect(() => {
    applyMarketFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMode, selectedSectors, selectedCompanies, selectedReliabilityLevels, selectedAumBuckets, employeeRange, marketData]);

  return (
    <div className="my-employees">
      {!embedded && (
        <div className="my-employees-header">
          <div>
            <h2 className="my-employees-title">
              <i className="fas fa-users" aria-hidden="true" />
              Мои сотрудники
            </h2>
            <p className="my-employees-subtitle">
              Данные остаются на вашем устройстве и не передаются в datacircle.
            </p>
          </div>
          <div style={{ color: '#1a237e', fontWeight: 700 }}>{countText}</div>
          <button
            type="button"
            className="my-employees-btn my-employees-btn-secondary"
            disabled={seedingTestData}
            onClick={onSeedTestData}
          >
            {seedingTestData ? 'Загрузка…' : 'Заполнить тестовыми данными'}
          </button>
        </div>
      )}

      {embedded && (
        <div className="my-employees-header my-employees-header-compact">
          <p className="my-employees-subtitle" style={{ margin: 0 }}>
            Данные остаются на вашем устройстве и не передаются в datacircle. {countText}.
          </p>
          <button
            type="button"
            className="my-employees-btn my-employees-btn-secondary"
            disabled={seedingTestData}
            onClick={onSeedTestData}
          >
            {seedingTestData ? 'Загрузка…' : 'Заполнить тестовыми данными'}
          </button>
        </div>
      )}

      {seedMessage && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid rgba(26, 35, 126, 0.25)',
            background: 'rgba(26, 35, 126, 0.06)',
            color: '#1a237e',
            fontSize: 13,
            fontWeight: 600,
          }}
          role="status"
        >
          {seedMessage}
        </div>
      )}

      <div
        ref={employeeFormCardRef}
        data-tour="employees-form"
        className={`my-employees-card${isEditing ? ' my-employees-card-editing' : ''}`}
      >
        {error && (
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid rgba(198, 40, 40, 0.35)',
              background: 'rgba(198, 40, 40, 0.08)',
              color: '#c62828',
              fontSize: 13,
              fontWeight: 600,
            }}
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="my-employees-form-header">
          {isEditing ? (
            <>
              <div className="my-employees-form-title">
                <i className="fas fa-pen" aria-hidden="true" />
                Редактирование: {fullName.trim() || editingEmployee?.fullName}
              </div>
              {(position.trim() || editingEmployee?.position) && (
                <div className="my-employees-form-subtitle">
                  {position.trim() || editingEmployee?.position}
                </div>
              )}
            </>
          ) : (
            <div className="my-employees-form-title">
              <i className="fas fa-user-plus" aria-hidden="true" />
              Добавить сотрудника
            </div>
          )}
        </div>

        {isEditing && (
          <p className="my-employees-form-hint" role="status">
            Изменения применятся к выделенной строке в таблице «Список сотрудников» после нажатия «Сохранить изменения».
          </p>
        )}

        <form className="my-employees-form" onSubmit={onSubmit}>
          <div className="my-employees-field">
            <label>Имя / метка</label>
            <input
              ref={fullNameInputRef}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Иван Иванов"
              autoComplete="off"
            />
          </div>

          <div className="my-employees-field">
            <label>Должность</label>
            <input
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="Например: Вице-президент"
              autoComplete="off"
            />
          </div>

          <div className="my-employees-field">
            <label>FIX (₽/мес, gross)</label>
            <input
              value={fixMonthlyGross}
              onChange={(e) => setFixMonthlyGross(e.target.value)}
              inputMode="decimal"
              placeholder="450000"
            />
          </div>

          <div className="my-employees-field">
            <label>STI (%)</label>
            <input
              value={stiPercent}
              onChange={(e) => setStiPercent(e.target.value)}
              inputMode="decimal"
              placeholder="25"
            />
          </div>

          <div className="my-employees-field">
            <label>LTIP</label>
            <select value={ltip} onChange={(e) => setLtip(e.target.value as 'yes' | 'no')}>
              <option value="no">Нет</option>
              <option value="yes">Да</option>
            </select>
          </div>

          <div className="my-employees-actions">
            <button
              type="submit"
              className="my-employees-btn my-employees-btn-primary"
              disabled={!canSubmit}
              title={!canSubmit ? 'Заполните обязательные поля' : submitLabel}
            >
              {submitLabel}
            </button>
            <button
              type="button"
              className="my-employees-btn my-employees-btn-secondary"
              onClick={resetForm}
              disabled={!isEditing && !fullName && !position && !fixMonthlyGross && !stiPercent && ltip === 'no'}
            >
              {isEditing ? 'Отменить редактирование' : 'Сбросить'}
            </button>
          </div>
        </form>

        
      </div>

      <div className="my-employees-card my-employees-roles-card">
        <div style={{ fontWeight: 800, color: '#1a237e', marginBottom: 12 }}>
          <i className="fas fa-briefcase" style={{ marginRight: 8 }} aria-hidden="true"></i>
          Список ролей
        </div>
        <div className="my-employees-table-wrap">
          <table className="my-employees-table my-employees-table-compact">
            <thead>
              <tr>
                <th>Должность</th>
                <th>Роль</th>
                <th>Family</th>
                <th style={{ textAlign: 'right' }}>Сотрудников</th>
                <th style={{ textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ color: '#666' }}>
                    Загрузка…
                  </td>
                </tr>
              ) : positionRoleRows.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ color: '#666' }}>
                    Должности появятся после добавления сотрудников.
                  </td>
                </tr>
              ) : (
                positionRoleRows.map((row) => {
                  const isHighlighted = rowHighlight?.positionKey === row.key;
                  const rowClassName = [
                    !row.roleName ? 'my-employees-role-row-ungraded' : '',
                    isHighlighted ? 'my-employees-row-highlighted' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                  <tr
                    key={row.key}
                    ref={isHighlighted ? highlightedRoleRowRef : undefined}
                    className={rowClassName || undefined}
                  >
                    <td style={isHighlighted ? HIGHLIGHT_CELL_STYLE : undefined}>{row.position}</td>
                    <td style={isHighlighted ? HIGHLIGHT_CELL_STYLE : undefined}>{row.roleName ?? '—'}</td>
                    <td style={isHighlighted ? HIGHLIGHT_CELL_STYLE : undefined}>{row.familyName ?? '—'}</td>
                    <td style={{ textAlign: 'right', ...(isHighlighted ? HIGHLIGHT_CELL_STYLE : {}) }}>{row.employeeCount}</td>
                    <td>
                      {onGradePosition ? (
                        <div className="my-employees-row-actions">
                          <button
                            type="button"
                            className="my-employees-link"
                            onClick={() => onGradePosition(row.position)}
                          >
                            {row.roleName ? 'Изменить грейд' : 'Грейдировать'}
                          </button>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`my-employees-card${isEditing ? ' my-employees-list-card-editing' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 800, color: '#1a237e' }}>
            <i className="fas fa-list" style={{ marginRight: 8 }} aria-hidden="true"></i>
            Список сотрудников
          </div>
          {employees.length > employeesVisibleLimit && (
            <button
              type="button"
              className="my-employees-link"
              onClick={() => setIsEmployeesListExpanded((v) => !v)}
            >
              {isEmployeesListExpanded ? 'Свернуть' : `Показать всех (${employees.length})`}
            </button>
          )}
        </div>
        {isEditing && (
          <div className="my-employees-editing-banner" role="status">
            Сейчас редактируете:{' '}
            <strong>{fullName.trim() || editingEmployee?.fullName}</strong>
            {position.trim() || editingEmployee?.position ? (
              <span> — {position.trim() || editingEmployee?.position}</span>
            ) : null}
          </div>
        )}
        <div className="my-employees-table-wrap">
          <table className="my-employees-table">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Должность</th>
                <th>FIX (₽/мес)</th>
                <th>STI (%)</th>
                <th>Total compensation (₽/год)</th>
                <th>LTIP</th>
                <th style={{ textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ color: '#666' }}>
                    Загрузка…
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ color: '#666' }}>
                    Пока нет сотрудников. Добавьте первого сверху.
                  </td>
                </tr>
              ) : (
                visibleEmployees.map((emp) => {
                  const isEditingRow = editingId === emp.id;
                  const isHighlighted = rowHighlight?.employeeId === emp.id;
                  const rowClassName = [
                    isEditingRow ? 'my-employees-row-editing' : '',
                    isHighlighted && !isEditingRow ? 'my-employees-row-highlighted' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const cellStyle = isHighlighted && !isEditingRow ? HIGHLIGHT_CELL_STYLE : undefined;
                  const rowRef = isHighlighted
                    ? highlightedEmployeeRowRef
                    : isEditingRow
                      ? editingEmployeeRowRef
                      : undefined;

                  return (
                  <tr
                    key={emp.id}
                    ref={rowRef}
                    className={rowClassName || undefined}
                  >
                    <td style={cellStyle}>{emp.fullName}</td>
                    <td style={cellStyle}>{emp.position}</td>
                    <td style={cellStyle}>{new Intl.NumberFormat('ru-RU').format(emp.fixMonthlyGross)}</td>
                    <td style={cellStyle}>{(emp.stiFraction * 100).toFixed(1)}</td>
                    <td style={cellStyle}>{new Intl.NumberFormat('ru-RU').format(Math.round((emp.fixMonthlyGross * 12) * (1 + emp.stiFraction)))}</td>
                    <td style={cellStyle}>{emp.ltip ? 'Да' : 'Нет'}</td>
                    <td>
                      <div className="my-employees-row-actions">
                        {onGradeEmployee && (
                          <button
                            type="button"
                            className="my-employees-link"
                            onClick={() => onGradeEmployee(emp)}
                          >
                            Изменить грейд
                          </button>
                        )}
                        <button
                          type="button"
                          className={`my-employees-link${isEditingRow ? ' my-employees-link-active' : ''}`}
                          onClick={() => onEdit(emp)}
                          aria-current={isEditingRow ? 'true' : undefined}
                        >
                          {isEditingRow ? 'Редактируется' : 'Редактировать'}
                        </button>
                        <button
                          type="button"
                          className="my-employees-link my-employees-link-danger"
                          onClick={() => onDelete(emp.id)}
                        >
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && employees.length > employeesVisibleLimit && !isEmployeesListExpanded && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
            Показано {Math.min(employeesVisibleLimit, employees.length)} из {employees.length}. Нажмите “Показать всех”, чтобы развернуть.
          </div>
        )}
      </div>

      <div className="my-employees-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1a237e' }}>
              <i className="fas fa-balance-scale" style={{ marginRight: 10 }} aria-hidden="true"></i>
              Сравнение с рынком
            </h3>
            <button
              type="button"
              className="my-employees-btn my-employees-btn-secondary"
              onClick={() => setIsMarketModalOpen(true)}
            >
              {selectedPackageDisplayName ? `Пакет: ${selectedPackageDisplayName}` : 'Выбрать пакет рынка'}
            </button>
          </div>
          <div className="my-employees-muted" style={{ fontSize: 12, color: '#666' }}>
            Сравнение по грейду из вкладки «Грейдирование»; учитываются фильтры ниже.
          </div>
        </div>

        {marketLoading ? (
          <div style={{ marginTop: 12, color: '#666' }}>Загрузка рыночных данных…</div>
        ) : selectedPackageDisplayName ? (
          <div style={{ marginTop: 14 }}>
            <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 800, color: '#1a237e' }}>
                  <i className="fas fa-sliders-h" style={{ marginRight: 8 }} aria-hidden="true"></i>
                  Фильтры рынка
                </div>
                <button
                  type="button"
                  className="my-employees-link"
                  onClick={() => setIsReliabilityModalOpen(true)}
                >
                  Что такое “надежность”?
                </button>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  <button
                    type="button"
                    className={`my-employees-btn my-employees-btn-secondary`}
                    onClick={() => setFilterMode('sector')}
                    style={{ borderColor: filterMode === 'sector' ? '#1a237e' : undefined }}
                  >
                    По секторам
                  </button>
                  <button
                    type="button"
                    className={`my-employees-btn my-employees-btn-secondary`}
                    onClick={() => setFilterMode('company')}
                    style={{ borderColor: filterMode === 'company' ? '#1a237e' : undefined }}
                  >
                    По компаниям
                  </button>
                </div>

                {filterMode === 'sector' && (
                  <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ fontSize: 13, color: '#333' }}>
                      Секторы (выбрано {selectedSectors.length}):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {Array.from(new Set(marketData.map((r) => r.sector))).filter(Boolean).map((sector) => (
                        <label key={sector} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={selectedSectors.includes(sector)}
                            onChange={() =>
                              setSelectedSectors((prev) =>
                                prev.includes(sector) ? prev.filter((s) => s !== sector) : [...prev, sector]
                              )
                            }
                          />
                          {sector}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {filterMode === 'company' && (
                  <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ fontSize: 13, color: '#333' }}>
                      Компании (выбрано {selectedCompanies.length}, минимум {MIN_SELECTED_COMPANIES}):
                    </div>
                    <div style={{ display: 'grid', gap: 10 }}>
                      {Array.from(companiesBySector.entries()).map(([sector, companies]) => (
                        <div key={sector}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: '#1a237e', marginBottom: 6 }}>
                            {sector}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>
                            {companies.map((c) => (
                              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                <input
                                  type="checkbox"
                                  checked={selectedCompanies.includes(c.id)}
                                  onChange={() =>
                                    setSelectedCompanies((prev) =>
                                      prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id]
                                    )
                                  }
                                />
                                {c.name}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedCompanies.length < MIN_SELECTED_COMPANIES && (
                      <div style={{ fontSize: 12, color: '#e65100' }}>
                        Выбрано менее {MIN_SELECTED_COMPANIES} компаний — сравнение будет недоступно.
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
                  <div style={{ fontSize: 13, color: '#333' }}>
                    Надежность (выбрано {selectedReliabilityLevels.length}):
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {RELIABILITY_LEVELS.map((lvl) => (
                      <label key={lvl.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={selectedReliabilityLevels.includes(lvl.id)}
                          onChange={() =>
                            setSelectedReliabilityLevels((prev) =>
                              prev.includes(lvl.id) ? prev.filter((x) => x !== lvl.id) : [...prev, lvl.id]
                            )
                          }
                        />
                        <span style={{ width: 12, height: 12, background: lvl.color, borderRadius: 2, display: 'inline-block' }} />
                        {lvl.label}
                      </label>
                    ))}
                  </div>
                </div>

                {filterMode === 'sector' && (
                  <>
                    {datasetMissingEmployeeScale && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: '10px 12px',
                          borderRadius: 8,
                          border: '1px solid #ffe082',
                          background: '#fff8e1',
                          color: '#6d4c41',
                          fontSize: 12,
                          lineHeight: 1.45,
                        }}
                        role="status"
                      >
                        <div>
                          В ответе API нет численности сотрудников (все значения пустые). Задеплойте актуальный backend с
                          полем <code style={{ fontSize: 11 }}>employee_amount</code> в <code style={{ fontSize: 11 }}>/market/records</code>.
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: 14 }}>
                      <DualRangeSlider
                        label="Количество сотрудников"
                        min={employeeBounds[0]}
                        max={employeeBounds[1]}
                        value={employeeRange}
                        onChange={setEmployeeRange}
                      />
                    </div>

                    <div style={{ marginTop: 14 }}>
                      <AumBucketFilter
                        selected={selectedAumBuckets}
                        onChange={setSelectedAumBuckets}
                        showActions={false}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <EmployeeMarketComparisonList
              employees={employees}
              filteredMarketData={filteredData}
              positionGradings={positionGradings}
              employeeGradingOverrides={employeeGradingOverrides}
              groupingMode={comparisonGroupingMode}
              onGroupingModeChange={setComparisonGroupingMode}
              clusterMetas={clusterMetas}
              clusterGroupingReady={clusterGroupingReady}
            />
          </div>
        ) : (
          <div style={{ marginTop: 12, color: '#666' }}>
            Выберите пакет рынка, чтобы включить сравнение.
          </div>
        )}
      </div>

      {isMarketModalOpen && (
        <DataPackagesModal
          onClose={() => setIsMarketModalOpen(false)}
          onPackageSelect={(name, display) => {
            setIsMarketModalOpen(false);
            handleSelectMarketPackage(name, display);
          }}
        />
      )}

      <ReliabilityModal
        isOpen={isReliabilityModalOpen}
        onClose={() => setIsReliabilityModalOpen(false)}
      />
    </div>
  );
};

export default MyEmployeesSection;

